import { dDom, rebind } from '../utils/renderUtils';
import { get } from 'object-path';

import { attachLoader, removeLoader } from './loaderUtil';

import { error, warn, debug, verbose } from './logger';
import { } from 'waypoints/lib/noframework.waypoints.js';

// Internal util functions
export function generate(config, store, overrides = {}, defaultOptions = {}) {
    // Combining the overrides is necessary here because if this component is a child component
    // which has inherited overrides and the parent also has inherited overrides, these overrides
    // must be merged.
    // Would make more sense to have inheritedOverrides as a 4th parameter...
    const mergedOverrides = mergeOverrides(config, overrides);
    const componentConfig = generateConfig(config, mergedOverrides);

    verbose(`Generating component named: ${componentConfig.name}`);

    let components = constructComponents(componentConfig, store, defaultOptions);
    let index = 0;

    const inherited = componentConfig.getInheritedOverrides();

    _.forEach(components, (constructedComponent) => {
        let children = {};

        _.forEach(constructedComponent.children, (child) => {
            let init = child;

            // eslint-disable-next-line no-shadow
            let overrides = {};
            let newChild = {};

            if (Array.isArray(child)) {
                init = child[0];
                overrides = child[1];
            }
            if (!overrides) {
                overrides = {};
            }
            overrides.parent = componentConfig;

            if (constructedComponent.multiple) {
                overrides.parentLocation = constructedComponent.componentLocation;
                overrides.multipleIndex = index;
                overrides.generateDictionary = constructedComponent.generateDictionary;
                overrides.beforeAttach = constructedComponent.beforeAttach;
            }

            overrides = mergeOverrides(overrides, inherited);

            newChild = init(store, overrides);
            children = Object.assign(
                {}, children, newChild
            );
        });

        if (Object.keys(children).length > 0) {
            constructedComponent.childrenObjects = children;
        }

        components = Object.assign(
            {}, components, children
        );
        index++;
    });

    verbose(`Generated component named: ${componentConfig.name}`);

    return components;
}

/*
 * Merge the inherited overrides from the parents into the children.
 * Do a bit of logic to maintain any overrides in the child as well
 * @param overrides - current overrides
 * @param inherited - overrides from the parent
 * @return new overrides, nicely merged
 */
function mergeOverrides(overrides, inherited) {
    if (!overrides.inheritedOverrides) {
        overrides.inheritedOverrides = [];
    }
    const childInheritedOverrides = overrides.inheritedOverrides.slice(0);
    const merged = _.merge(overrides, inherited);

    merged.inheritedOverrides.push(...childInheritedOverrides);

    // No duplicates in the inherited properties
    merged.inheritedOverrides = _.uniq(merged.inheritedOverrides);

    return merged;
}

/**
 * Returns the inherited overrides of this component.
 * Useful for children construction and modals
 * @return {Object} Component overrides
 */
export function getInheritedOverrides() {
    const inheritedOverrides = this.inheritedOverrides || [];

    // This pushes inheritedOverrides down to all children, grand-children, and deeper
    inheritedOverrides.push('inheritedOverrides');

    return _.reduce(inheritedOverrides, (result, value) => {
        result[value] = this[value];
        return result;
    }, {});
}

export function constructComponents(config, store, defaultOptions) {
    // If isComponentValid returns false return an empty object
    // which will stop the component from constructing
    if (!config.isComponentValid(store)) {
        return {};
    }

    config.beforeConstruction(store);

    let components = {};

    if (config.multiple) {
        components = attachMultiple(config, components, store, defaultOptions);
    } else {
        components = attachSingle(config, components, store, defaultOptions);
    }

    runFirstRenders(components, store);
    runAttachEvents(components, store);
    runAttachWatchers(components, store);
    runAfterConstruction(components, store);
    runLazyLoaders(components, store);
    return components;
}

export function runAttachEvents(components, store) {
    _.forEach(components, (component) => {
        component.beforeAttachEvents();
        verbose(`Running attachEvents for ${component.name}`);
        component.attachEvents(store, component.multipleIndex || 0);
        component.afterAttachEvents();
    });
}

export function runAttachWatchers(components, store) {
    _.forEach(components, (component) => {
        component.beforeAttachWatchers();
        verbose(`Running attachWatchers for ${component.name}`);
        component.removeWatchers = component.attachWatchers(store);
        component.afterAttachWatchers();
    });
}

export function runAfterConstruction(components, store) {
    _.forEach(components, (component) => {
        if (component.lazyLoad === false) {
            verbose(`Running afterConstruction for ${component.name}`);
            component.afterConstruction(store);
        }
    });
}

export function runLazyLoaders(components, store) {
    _.forEach(components, (component) => {
        if (component.lazyLoad) {
            verbose(`Creating Waypoint lazyload for ${component.name}`);
            new Waypoint({ // eslint-disable-line no-new, no-undef
                element: document.querySelector(component.templateLocation),
                handler: function waypointHandler() {
                    component.afterConstruction(store);
                    this.destroy();
                },
                offset: 'bottom-in-view'
            });
        }
    });
}

export function runFirstRenders(components, store) {
    _.forEach(components, (component) => {
        verbose(`Running firstRender for ${component.name}`);
        component.firstRender(store);
    });
}

export function generateConfig(config, overrides) {
    return Object.assign(
        {}, defaults, config, overrides
    );
}

/**
 * Create mulitple component overrides for components with
 * multiple similar children i.e. Family Products
 * @param  {Object} config      Component config
 * @param  {Array} components   List of components to override
 * @param  {Object} store       A15 Redux store
 * @param {Object} defaultOptions additional properties to be added to each new component
 * @return {Array}              List of overridden components
 */
export function attachMultiple(config, components, store, defaultOptions) {
    if (!config.multiple) {
        return;
    }
    const multiples = config.multiple(store);

    for (let index = 0; index < multiples; index++) {
        const newLocation = `${config.templateLocation}[data-index='${index}']`;

        const multiConfig = {
            store: store,
            componentLocation: newLocation,
            multipleIndex: index
        };

        if (config.multiwatch) {
            multiConfig.watch = config.multiwatch(index);
        }

        components[newLocation] = Object.assign(
            {}, config, multiConfig, defaultOptions
        );
    }

    // eslint-disable-next-line consistent-return
    return components;
}

export function attachSingle(config, components, store, defaultOptions) {
    const updatedConfig = config.beforeAttach(config, components);

    if (updatedConfig) {
        // eslint-disable-next-line no-param-reassign
        config = updatedConfig;
    }

    if (config.parentLocation) {
        config.templateLocation = `${config.parentLocation} ${config.templateLocation}`;
    }

    let componentName = config.name;

    if (config.templateLocation) {
        componentName = config.templateLocation;
    }

    components[componentName] = Object.assign(
        {}, config, {
            store: store,
            componentLocation: config.templateLocation
        },
        defaultOptions
    );

    return components;
}

// Default attached methods
export function updateChildren(componentList, store) {
    this.removeChild(_.difference(this.children, componentList), store);
    this.addChild(_.difference(componentList, this.children), store);
    this.children = componentList;
}

export function addChildren(newChildren, store) {
    const parent = Object.assign({}, this);

    _.forEach(newChildren, (child) => {
        this.addChild(child, store, parent);
    });
    return this.childrenObjects;
}

export function addChild(child, store, parent = {}) {
    verbose(`Adding child component ${child.name}`);
    let overrides = {};
    let init;

    init = child;
    if (Array.isArray(child)) {
        init = child[0];
        overrides = child[1];
    }

    overrides = mergeOverrides(overrides, this.getInheritedOverrides());
    overrides.parent = parent;

    let newComponent;

    if (_.isFunction(init)) {
        newComponent = init(store, overrides);
        this.childrenObjects = Object.assign(
            {}, this.childrenObjects, newComponent
        );
    } else {
        newComponent = undefined; // eslint-disable-line no-undefined
    }
    return newComponent;
}

export function removeAllChildren() {
    const self = this;

    _.forEach(this.childrenObjects, (child) => {
        self.removeChild(child);
        child.removeAllChildren();
    });

    this.childrenObjects = {};
}

export function removeChild(child, store) {
    verbose(`Removing child component ${child.name}`);
    deactivateChild(child, store);
    this.childrenObjects = _.omit(this.childrenObjects, child);
}

export function deactivateChild(child, store) {
    verbose(`Deactivaing child component ${child.name}`);
    if (!child.removeWatchers) {
        debug(`component ${child.name} does not have a removeWatchers...`);

        // This happens when they don't have watchers?
    } else {
        verbose(`Removing watchers for child component ${child.name}`);
        child.removeWatchers(store);
    }

    child.removeEvents(store);
}

export function pauseChildren(store) {
    _.forEach(this.childrenObjects, (child) => {
        this.pauseChild(child, store);
    });
}

export function pauseChild(child, store) {
    verbose(`Pausing child component ${child.name}`);
    if (!child.removeWatchers) {
        debug(`component ${child.name} does not have a removeWatchers...`);

        // This happens when they don't have watchers?
    } else {
        verbose(`Removing watchers for child component ${child.name}`);
        child.removeWatchers(store);
    }
}

export function unpauseChildren(store) {
    _.forEach(this.childrenObjects, (child) => {
        this.unpauseChild(child, store);
    });
}

export function unpauseChild(child, store) {
    verbose(`Unpausing child component ${child.name}`);
    child.removeWatchers = child.attachWatchers(store);

    // force for case where state changes during pause
    child.onStateChange(store);
}

export function reactivateChild(child, store) {
    verbose(`Reactivating child component ${child.name}`);
    child.attachWatchers(store);
    child.attachEvents(store, child.multipleIndex || 0);
}

export function rebindChildren(store) {
    let index = 0;
    let elements;
    let el;

    _.forEach(this.childrenObjects, (child) => {
        if (child.multiple) {
            elements = $(child.templateLocation);
            el = elements[index];
            index++;
        } else {
            el = $(`${child.componentLocation}`);
        }
        rebind(child, el, store);
    });
}

/**
 * Renders a component's view model according to its configuration options
 * @param {Object} store      A15 redux store
 * @param {Object} config     Component configuration options
 * @param {Object} dictionary View model to render
 */
export function render(store, config, dictionary) {
    this.beforeRender(store);

    verbose(`Running render method for ${this.name}`);
    let templateString;
    let updatedTemplateString;

    if (!window.envVars.performanceDebug) {
        templateString = generateTemplate(store, config, dictionary);
        updatedTemplateString = this.beforeInsertion(store, templateString);

        this.renderType(config, updatedTemplateString, store, this.renderOptions);
    } else {
        let timeStart = (new Date()).getTime();
        templateString = generateTemplate(store, config, dictionary);
        let timeDiff = (new Date()).getTime() - timeStart;

        warn(`${this.name} template: ${timeDiff}ms`);

        updatedTemplateString = this.beforeInsertion(store, templateString);

        timeStart = (new Date()).getTime();
        this.renderType(config, updatedTemplateString, store, this.renderOptions);
        timeDiff = (new Date()).getTime() - timeStart;
        warn(`${this.name} render: ${timeDiff}ms`);
    }

    this.finalRenderCall();
    if (this.rebind) {
        this.rebindChildren(store);
    }
}

export function generateTemplate(store, config, dictionary) {
    const model = Object.assign(
        {}, store.getState(), config, config.generateDictionary(store.getState(), dictionary, config));

    return config.template.render(model);
}

export function generateDictionary(state, dict = {}, config) {
    return Object.assign({}, state, dict);
}

export function removeWatchers() {}

export function attachEvents() {}

export function removeEvents(store) {
    verbose(`Removing events for ${this.name}`);
    const eventTypes = ['touchend', 'click', 'mouseup', 'mousedown', 'mouseenter', 'mouseleave', 'submit', 'change',
        'keydown', 'keyup'];

    for (let i = 0; i < eventTypes.length; i++) {
        const event = eventTypes[i];
        const cleanTemplateLocation = templateLocationForEvent(this.templateLocation);

        $(document).off(`${event}.${cleanTemplateLocation}`);
    }
}

export function beforeInsertion(store, templateString) {
    return templateString;
}

export function destroyComponent() {}

// lifecycle functions
export function isComponentValid(store) {
    return true;
}

export function beforeConstruction() {}

export function afterConstruction() {}

export function beforeAttachEvents() {}

export function afterAttachEvents() {}

export function beforeAttachWatchers() {}

export function afterAttachWatchers() {}

export function beforeRender() {}

export function afterRender() {}

export function firstRender() {}

export function finalRenderCall() { }

export function afterRebind() {}

/**
 * Clean a templateLocation so that it can be used on a scoped events
 *
 * For instance ".js-uuid-XXX .dom-modal-contents" is tranformed to
 * "js-uuid-XXXXdom-modal-contents"
 * @param {String} templateLocation a template location string
 * @return {String} the transformed template location string
 */
export function templateLocationForEvent(templateLocation) {
    if (!templateLocation) {
        return '';
    }
    return templateLocation.replace(/[.\s]/g, '');
}

/**
 * Scopes events with the templateLocation
 * @param {Array} eventTypes    'touchend' or 'touchend click'
 * @return {String}             List of template locations
 */
export function scopedEvent(eventTypes) {
    const bits = eventTypes.split(/\s+/);
    const bitsWithTemplate = _.map(bits, (bit) => {
        const cleanTemplateLocation = templateLocationForEvent(this.componentLocation);

        return `${bit}.${cleanTemplateLocation}`;
    });

    return bitsWithTemplate.join(' ');
}

/**
 * Scopes a css selector with the templateLocation
 * @param {String} selectors     '.js-blah' or '.js-blah, .js-blah2'
 * @returns {String}            Target selector
 */
export function scopedSelector(selectors) {
    if (!selectors) {
        return this.templateLocation;
    }

    const bits = selectors.split(/,/);
    const bitsWithTemplate = _.map(bits, (bit) => `${this.componentLocation} ${bit}`);

    return bitsWithTemplate.join(',');
}

export function beforeAttach() {}

/**
 * Subscribes a component to state changes, and attaches a handler to manage when to updateChildren
 * @param {Object} store    A15 redux store
 * @return {Function}       Redux unsubscribe function
 */
export function attachWatchers(store) {
    if (_.flatten([this.watch]).length < 1) {
        return null;
    }
    let previousState = {};

    const watchValues = _.map(_.flatten([this.watch]), (w) => {
        if (_.isFunction(w)) {
            return w.call(this);
        }
        return w;
    });

    for (let watchIndex = 0; watchIndex < watchValues.length; watchIndex++) {
        const watchValue = watchValues[watchIndex];

        previousState[watchValue] = _.cloneDeep(get(store.getState(), watchValue));

        if (_.isString(watchValue) && watchValue.split('.').length <= 1) {
            error(`Watch value ${watchValue} on ${this.name} is too broad`);
        }
    }

    function handleStateChange() {
        let timeStart = 0;

        if (window.envVars.performanceDebug) {
            timeStart = (new Date()).getTime();
        }

        const currentState = {};

        for (let watchIndex = 0; watchIndex < watchValues.length; watchIndex++) {
            const watchValue = watchValues[watchIndex];

            currentState[watchValue] = _.cloneDeep(get(store.getState(), watchValue));

            if (this.checkStateChange(previousState, currentState, watchValue)) {
                verbose(`Change in ${watchValue} detected for component ${this.name}`);
                this.onStateChange(store, watchValue, previousState[watchValue], currentState[watchValue]);
                break;
            }
        }
        previousState = currentState;

        if (window.envVars.performanceDebug) {
            const timeDiff = (new Date()).getTime() - timeStart;

            if (timeDiff < 20) {
                warn(`${this.name} onStateChange: ${timeDiff}ms`);
            } else {
                error(`${this.name} onStateChange: ${timeDiff}ms -- very slow`);
            }
        }
    }

    return store.subscribe(handleStateChange.bind(this));
}

/**
 * Default function executed when watched part of store changes
 * @param {Object} store - the redux store
 * @param {String} watchValue the watch value string that changed
 * @param {Object} previous the previous value of the watch value
 * @param {Object} current the new value of the watch value
 * @return {Boolean} true if everything went as expected, false if a bad state was found
 */
export function onStateChange(store, watchValue, previous, current) {
    this.render(store, this, {});
    return true;
}

/**
 * Iterates through watchValue array looking for changed state, returns first watch value with changed state
 * @param {Object} previousState    The store state prior to subscription being triggered
 * @param {Object} currentState     The store state after the subscription update
 * @param {String} watchValue       Watch key to watch
 * @return {Object}                 State change
 */
export function checkStateChange(previousState, currentState, watchValue) {
    return !_.isEqual(previousState[watchValue], currentState[watchValue]);
}

/**
 * Merge a component config and a brand override config
 * @param  {Object} config         Component config
 * @param  {Object} overrideConfig Component Brand Override config
 * @return {Object}                Merged config
 */
export function mergeConfigOverrides(config, overrideConfig) {
    return _.mergeWith(config, overrideConfig, mergeConfigOverridesCustomizer);
}

/**
 * Custom merge function for a component config and a brand override config
 * @param  {*} configValue          Component config value to be merged
 * @param  {*} overrideConfigValue  Component Brand Override config value to be merged
 * @return {*}                      Merged value
 */
export function mergeConfigOverridesCustomizer(configValue, overrideConfigValue) {
    if (_.isArray(configValue) && _.isArray(overrideConfigValue)) {
        return _.concat(configValue, overrideConfigValue);
    }

    if (_.isObject(configValue) && _.isObject(overrideConfigValue)) {
        return mergeConfigOverrides(configValue, overrideConfigValue);
    }

    return overrideConfigValue;
}

/**
 * Generic test for template location, to be used (usually) in isComponentValid
 *
 * to be used in the config like:
 *   componentValid: templateLocationExists
 *   @return {Boolean} true if template location exists in the DOM, false otherwise
 */
export function templateLocationExists() {
    const f = function findTemplate() {
        return $(this.templateLocation).length > 0;
    }.bind(this);

    return f();
}

// Default configuration object for components
export const defaults = {
    children: [],
    childrenObjects: {},
    attachEvents: attachEvents,
    attachWatchers: attachWatchers,
    removeWatchers: removeWatchers,
    removeEvents: removeEvents,
    addChild: addChild,
    addChildren: addChildren,
    removeAllChildren: removeAllChildren,
    removeChild: removeChild,
    deactivateChild: deactivateChild,
    reactivateChild: reactivateChild,
    pauseChild: pauseChild,
    pauseChildren: pauseChildren,
    unpauseChild: unpauseChild,
    unpauseChildren: unpauseChildren,
    rebindChildren: rebindChildren,
    updateChildren: updateChildren,
    template: () => {},
    name: 'genericComponent',
    watch: [],
    render: render,
    firstRender: firstRender,
    beforeRender: beforeRender,
    afterRender: afterRender,
    beforeConstruction: beforeConstruction,
    afterConstruction: afterConstruction,
    beforeAttachEvents: beforeAttachEvents,
    afterAttachEvents: afterAttachEvents,
    beforeAttachWatchers: beforeAttachWatchers,
    afterAttachWatchers: afterAttachWatchers,
    rebind: false,
    finalRenderCall: finalRenderCall,
    renderType: dDom,
    generateDictionary: generateDictionary,
    beforeAttach: beforeAttach,
    scopedEvent: scopedEvent,
    scopedSelector: scopedSelector,
    beforeInsertion: beforeInsertion,
    getInheritedOverrides: getInheritedOverrides,
    isComponentValid: isComponentValid,
    checkStateChange: checkStateChange,
    onStateChange: onStateChange,
    afterRebind: afterRebind,
    attachLoader: attachLoader,
    removeLoader: removeLoader,
    loader: {},
    multiple: null,
    lazyLoad: false
};
